import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { TrendingUp, IndianRupee, Star, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

import { AdminShell } from '../components/hotelAdmin/AdminShell';
import { HomeSection, ReservationsSection } from '../components/hotelAdmin/SectionsCore';
import { CalendarSection, RatesSection, PropertySection, PhotosSection } from '../components/hotelAdmin/SectionsEdit';
import { ReviewsSection, FinanceSection, InsightsSection, InboxSection } from '../components/hotelAdmin/SectionsMore';
import { RoomsSection } from '../components/hotelAdmin/RoomsSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HotelDashboard = () => {
  const { user, checkAuth } = useAuth();
  const [section, setSection] = useState('home');
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [insights, setInsights] = useState(null);
  const [unreadInbox, setUnreadInbox] = useState(0);

  const reloadBookings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/hotel/bookings`, { withCredentials: true });
      setBookings(data);
    } catch (err) { console.error('[HotelDashboard] reloadBookings failed:', err); }
  }, []);

  const reloadRooms = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/hotel/room-types`, { withCredentials: true });
      setRooms(data);
    } catch (err) { console.error('[HotelDashboard] reloadRooms failed:', err); }
  }, []);

  const reloadRatings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/hotel/ratings`, { withCredentials: true });
      setRatings(data);
    } catch (err) { console.error('[HotelDashboard] reloadRatings failed:', err); }
  }, []);

  const reloadInsights = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/hotel/insights`, { withCredentials: true });
      setInsights(data);
    } catch (err) { console.error('[HotelDashboard] reloadInsights failed:', err); }
  }, []);

  const reloadInbox = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/hotel/inbox`, { withCredentials: true });
      setUnreadInbox(data.reduce((s, t) => s + (t.unread || 0), 0));
    } catch (err) { console.error('[HotelDashboard] reloadInbox failed:', err); }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'hotel') return;
    reloadBookings();
    reloadRooms();
    reloadRatings();
    reloadInsights();
    reloadInbox();
    const t = setInterval(reloadInbox, 20000);
    return () => clearInterval(t);
  }, [user, reloadBookings, reloadRooms, reloadRatings, reloadInsights, reloadInbox]);

  if (!user || user.role !== 'hotel') {
    return <div className="p-10 text-center text-sm text-gray-500">You need to sign in as a hotel admin to view this page.</div>;
  }

  const headerStats = [
    { label: 'Bookings · 30d', value: insights?.totals?.bookings_30d ?? 0, icon: BookOpen },
    { label: 'Revenue · 30d', value: `₹${Number(insights?.totals?.revenue_30d ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, icon: IndianRupee },
    { label: 'Avg. rating', value: `${(insights?.totals?.avg_rating ?? 0).toFixed(1)} /10`, icon: Star },
    { label: 'Active rooms', value: rooms.length, icon: TrendingUp },
  ];

  return (
    <AdminShell activeId={section} onNav={setSection} unreadInbox={unreadInbox} headerStats={headerStats}>
      {section === 'home' && <HomeSection bookings={bookings} onNav={setSection} insights={insights} />}
      {section === 'reservations' && <ReservationsSection bookings={bookings} reloadBookings={reloadBookings} />}
      {section === 'calendar' && <CalendarSection rooms={rooms} reloadRooms={reloadRooms} />}
      {section === 'rooms' && <RoomsSection rooms={rooms} reloadRooms={reloadRooms} />}
      {section === 'rates' && <RatesSection rooms={rooms} reloadRooms={reloadRooms} user={user} reloadUser={checkAuth} />}
      {section === 'property' && <PropertySection user={user} reloadUser={checkAuth} />}
      {section === 'photos' && <PhotosSection user={user} reloadUser={checkAuth} />}
      {section === 'reviews' && <ReviewsSection ratings={ratings} reloadRatings={reloadRatings} />}
      {section === 'finance' && <FinanceSection />}
      {section === 'insights' && <InsightsSection insights={insights} />}
      {section === 'inbox' && <InboxSection />}
    </AdminShell>
  );
};
